import { t } from '@lingui/core/macro';
import { FieldType, type ConfigSchemaProperty, type DataSourceChoice, type DataDriver, type DriverProfile, type SpecConfigField } from '@sdk';

/**
 * The create form's logic, over a manifest the BACKEND supplies.
 *
 * This file used to be `provider-catalog.ts` and hardcoded every provider's
 * fields as literal strings, because the driver registry had no list accessor
 * and no route. It does now: a source is a `data_driver` asset, so the
 * form reads the spec's `config` and a new source lights the dialog up with no
 * frontend release.
 *
 * **Pure — no React, no SDK calls.** Specs arrive as arguments rather than a
 * module constant, so every rule below is testable without rendering anything
 * or standing up a backend.
 *
 * The per-provider validators are gone with the catalog. `isHttpUrl` for RSS
 * and a Slack channel-id regex used to live here as `if (provider === …)`
 * branches; both are now a `pattern` on the field, which is the same check
 * expressed once instead of once per provider.
 */

/** Mirror of `MIN_POLL_INTERVAL_SECONDS` (flow_sdk/builtin/data_source.py),
 *  enforced there by `APIField(ge=60)`. Duplicated only to turn a 422 into a
 *  sentence the user can act on. */
export const MIN_POLL_INTERVAL_SECONDS = 60;

export interface SourceDraft {
  name: string;
  provider: string;
  account_key: string;
  enabled: boolean;
  open_to_all: boolean;
  poll_interval_seconds: number;
  window_days: number;
  /** Quiet this long ends a thread; the next message starts a new one. Null = never. */
  thread_timeout_seconds: number | null;
  /** A file source pulls only and never writes back. */
  read_only: boolean;
  /** A file source's local copy is kept out of git. */
  gitignored: boolean;
  /** Raw strings straight off the inputs; `buildConfig` types them. */
  fields: Record<string, string>;
  /**
   * What was PICKED, for the fields a manifest marks `choices`. A second map rather than
   * a richer `fields` value: a picked entry is `{id, name}`, and smuggling an object
   * through the string bag is exactly what makes `fieldValue` render `[object Object]`.
   * Empty for a field the user typed into instead — which is what makes the fallback work
   * with no mode flag anywhere in this file.
   */
  picked: Record<string, DataSourceChoice[]>;
}

/** `[key, field]` pairs in declaration order — the order the form renders. */
export function specFields(spec?: DataDriver): [string, SpecConfigField][] {
  return Object.entries(spec?.config ?? {});
}

/** The rules for one field, read off the driver's `Config` schema: whether it is required, the
 *  pattern a typed value (each entry, for a list) must match, and its default. */
export function fieldRules(spec: DataDriver | undefined, key: string): { required: boolean; pattern?: string; default?: unknown } {
  const schema = spec?.config_schema;
  const prop: ConfigSchemaProperty = schema?.properties?.[key] ?? {};
  const patternOf = (p?: ConfigSchemaProperty): string | undefined =>
    p?.pattern ?? p?.anyOf?.map((a) => a.pattern).find(Boolean);
  return {
    required: (schema?.required ?? []).includes(key),
    pattern: patternOf(prop) ?? patternOf(prop.items),
    default: prop.default,
  };
}

/** A new source's draft for `spec` — its provider, and the fields its `Config` gives a `default`. `profile` is
 *  what the driver said about this way of connecting right now (`Source.profile()`), when the form has it. */
export function emptyDraft(spec?: DataDriver, profile?: DriverProfile | null): SourceDraft {
  return {
    // The driver's own title, so nothing is required of a person who has nothing to say about
    // the name — an empty box under a red "Name is required." is the form asking for a word it
    // could supply itself.
    // A driver may name what a new source IS ("Flow WhatsApp agent") better than its title does — and one that
    // names it for THIS person ("Eran's inbox", the same name its Slack app gets) says so in its profile, so
    // the source and the app are named once, in the driver.
    name: profile?.default_name || spec?.default_name || spec?.title || '',
    provider: spec?.name ?? '',
    // Empty means "derive from the fields" — `accountKeyFor` owns the default,
    // so exactly one place knows it.
    account_key: '',
    enabled: true,
    open_to_all: false,
    poll_interval_seconds: 300,
    window_days: 7,
    thread_timeout_seconds: null,
    read_only: false,
    gitignored: true,
    // A manifest `default` is what a new source starts with, not a hint the user must retype —
    // rendered the way an edited source's stored value is, so create and edit show the same text.
    fields: Object.fromEntries(
      specFields(spec)
        .map(([key, field]) => [key, fieldValue(key, field, { [key]: fieldRules(spec, key).default })] as const)
        .filter(([, value]) => value !== ''),
    ),
    picked: {},
  };
}

/** Config value → the string its input shows. Arrays rejoin the way they split. */
export function fieldValue(key: string, field: SpecConfigField, config: Record<string, unknown>): string {
  const raw = config?.[key];
  if (raw === undefined || raw === null) return '';
  // A choosable field's entries may be `{id, name}`. Joining those directly is how a
  // Slack source configured with named channels rendered as `[object Object]` — and then
  // SAVED that back over the real ids.
  //
  // IDs, not names, even though a name is friendlier: this string is only ever shown in
  // the TYPED fallback, and whatever sits there is what gets stored the moment someone
  // edits it. Showing "Marketing" in a box whose next keystroke saves "Marketing" as a
  // drive id is a silent corruption. The name belongs to the picker, which reads `picked`.
  if (field.choices) {
    const picked = pickedFrom(key, field, config);
    return picked.map((c) => c.id).join(field.type === FieldType.LINES ? '\n' : ', ');
  }
  if (Array.isArray(raw)) return raw.join(field.type === FieldType.LINES ? '\n' : ', ');
  // Only scalars round-trip through an input. A nested object in config means
  // the driver grew a shape this form does not model — show nothing rather than
  // "[object Object]", which would be saved back verbatim and corrupt it.
  return scalarText(raw);
}

function scalarText(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return '';
}

/**
 * One stored config entry, read as a choice.
 *
 * A bare string is `{id, name}` with both halves equal — that is a bucket, whose name IS
 * its id, and it is also every value stored before the picker existed. Anything else that
 * carries an `id` is what the picker writes. Returns null for a shape this form does not
 * model, so a caller drops it rather than rendering `[object Object]` and saving it back.
 */
export function choiceOf(raw: unknown): DataSourceChoice | null {
  if (typeof raw === 'string') return raw.trim() ? { id: raw.trim(), name: raw.trim() } : null;
  if (raw && typeof raw === 'object' && 'id' in raw) {
    const entry = raw as { id?: unknown; name?: unknown };
    const id = scalarText(entry.id).trim();
    return id ? { id, name: scalarText(entry.name) || id } : null;
  }
  return null;
}

/** A field's stored value as choices — one entry for a `text` field, many for `lines`. */
export function pickedFrom(
  key: string,
  field: SpecConfigField,
  config: Record<string, unknown>,
): DataSourceChoice[] {
  if (!field.choices) return [];
  const raw = config?.[key];
  const entries = Array.isArray(raw) ? raw : raw === undefined || raw === null ? [] : [raw];
  return entries.map(choiceOf).filter((c): c is DataSourceChoice => c !== null);
}

/**
 * What was picked for one field, if picking even applies to it.
 *
 * The "a pick wins over the text box" rule, in one place. Every reader of a draft has to
 * ask it — `buildConfig`, `accountKeyFor`, `validateDraft` and the dialog's own render —
 * and restating `field.choices ? … : []` at each of them is four chances for one of them
 * to drift into honouring a stale pick on a field that no longer offers any.
 */
export function pickedIn(
  draft: SourceDraft,
  key: string,
  field: SpecConfigField,
): DataSourceChoice[] {
  return field.choices ? (draft.picked[key] ?? []) : [];
}

/** What a picked choice is STORED as: a bare id when the name adds nothing. */
const storedChoice = (c: DataSourceChoice): string | { id: string; name: string } =>
  c.name && c.name !== c.id ? { id: c.id, name: c.name } : c.id;

const splitLines = (raw: string): string[] =>
  raw.split('\n').map((s) => s.trim()).filter(Boolean);

const splitCsv = (raw: string): string[] =>
  raw.split(',').map((s) => s.trim()).filter(Boolean);

/** Compiled patterns, keyed by the pattern itself. `validateDraft` re-runs on
 *  every keystroke, and a manifest's patterns are immutable, so compiling per
 *  call was pure waste. */
const PATTERNS = new Map<string, RegExp>();

function patternFor(pattern: string): RegExp {
  let re = PATTERNS.get(pattern);
  if (!re) {
    re = new RegExp(pattern);
    PATTERNS.set(pattern, re);
  }
  return re;
}

/**
 * The driver-specific half of the entity.
 *
 * Empty values are OMITTED rather than written as `""`/`null`: every optional
 * key has a real default inside the driver, and an empty string is not absent —
 * it would override the default with nothing.
 *
 * `kind` and `channel` are deliberately absent from everything this produces.
 * `sync_source` writes both from the driver on the first poll, so a form-set
 * value is authoritative-looking, owned by nobody, and silently corrected later.
 */
export function buildConfig(draft: SourceDraft, spec?: DataDriver): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  for (const [key, field] of specFields(spec)) {
    // A pick wins over the text box: the two are never both filled, because hand-editing
    // a choosable field clears its picks. A single-value field stores the one entry
    // itself, never a list — `bucket` is a string in config and must stay one.
    const picked = pickedIn(draft, key, field);
    if (picked.length) {
      config[key] =
        field.type === FieldType.LINES ? picked.map(storedChoice) : storedChoice(picked[0]);
      continue;
    }
    const raw = (draft.fields[key] ?? '').trim();
    if (!raw) continue;
    if (field.type === FieldType.LINES) config[key] = splitLines(raw);
    else if (field.type === FieldType.CSV) config[key] = splitCsv(raw);
    else if (field.type === FieldType.BOOL) config[key] = raw === 'true';
    else if (field.type === FieldType.NUMBER) {
      const n = Number(raw);
      if (!Number.isNaN(n)) config[key] = n;
    } else config[key] = raw;
  }
  return config;
}

/**
 * The account this draft names. An explicit entry always wins.
 *
 * Descriptive only: ids are uuid4 and nothing dedupes on this, so a wrong one
 * is a plain edit. A spec with no `account_key` field has no account to name —
 * Slack's case, where the workspace belongs to the connection, not the form.
 */
export function accountKeyFor(draft: SourceDraft, spec?: DataDriver): string {
  const explicit = draft.account_key.trim();
  if (explicit) return explicit;
  const named = specFields(spec).find(([, f]) => f.account_key);
  if (!named) return '';
  const [key, field] = named;
  // The ID names the account, never the display name — a renamed channel is the same
  // channel, and letting the label name the account would rename the source with it.
  const picked = pickedIn(draft, key, field);
  if (picked.length) return picked[0].id;
  const raw = (draft.fields[key] ?? '').trim();
  // A multi-value field names the account by its FIRST value: appending a feed
  // must not silently rename the source.
  return field.type === FieldType.LINES ? (splitLines(raw)[0] ?? '') : raw;
}

/**
 * Everything wrong with this draft, in the order a person would fix it.
 *
 * Deliberately NOT a uniqueness check. Ids are uuid4, two sources may point at
 * the same account, and that is allowed — the cost of a second poller is the
 * operator's call, not this form's.
 */
/** A new source of a driver that declares setup wizards: the wizard asks for its config, so the
 *  Add form asks only for a name. Editing an existing source keeps the whole form. */
export function setUpByWizard(spec: DataDriver | undefined, editing: boolean): boolean {
  return !editing && !!spec?.setup_wizards?.length;
}

export function validateDraft(draft: SourceDraft, spec?: DataDriver, { config = true } = {}): string[] {
  const problems: string[] = [];

  if (!draft.name.trim()) problems.push('Name is required.');
  if (!spec) problems.push(t`Unknown provider ${draft.provider}.`);

  for (const [key, field] of config ? specFields(spec) : []) {
    if (key === 'allowed_senders' && draft.open_to_all) continue;
    const raw = (draft.fields[key] ?? '').trim();
    const label = field.label || key;
    // A pick satisfies "has a value" on its own, and needs no pattern check: it came off
    // a list the provider itself just returned, so a regex here could only reject a value
    // the provider says is real.
    const picked = pickedIn(draft, key, field);
    if (picked.length) continue;
    const rules = fieldRules(spec, key);
    if (rules.required && !raw) {
      problems.push(t`${label} is required.`);
      continue;
    }
    if (!raw || !rules.pattern) continue;
    // One regex, applied per value, so a multi-line field reports the exact
    // entries at fault rather than "something is wrong".
    const re = patternFor(rules.pattern);
    const values =
      field.type === FieldType.LINES ? splitLines(raw) : field.type === FieldType.CSV ? splitCsv(raw) : [raw];
    const bad = values.filter((v) => !re.test(v));
    if (bad.length) problems.push(`${label}: not valid — ${bad.join(', ')}`);
  }

  if (draft.poll_interval_seconds < MIN_POLL_INTERVAL_SECONDS) {
    problems.push(
      `Poll interval must be at least ${MIN_POLL_INTERVAL_SECONDS}s — the heartbeat only ticks once a minute.`,
    );
  }
  if (draft.window_days < 1) problems.push('Window must be at least 1 day.');
  if (draft.thread_timeout_seconds !== null && !(draft.thread_timeout_seconds >= 60)) {
    problems.push('Thread timeout must be at least 1 minute — or empty, for threads that never time out.');
  }

  return problems;
}
